import { S } from '../app/app_state.js?v=20260817c';
import { cloneDeep, recordUndo } from '../services/history_service.js?v=20260903c';
import { isPreparingProjectMutation, prepareProjectMutation } from './project_mutation_scope.mjs';
import { copyProjectStateContext } from './project_state_service.js?v=20260904f';

function freezeTree(value) {
  if (value && typeof value === 'object') {
    Object.values(value).forEach(freezeTree);
    Object.freeze(value);
  }
  return value;
}

function changedKeys(before, after) {
  return [...new Set([...Object.keys(before), ...Object.keys(after)])]
    .filter(key => JSON.stringify(before[key]) !== JSON.stringify(after[key]));
}

/** Prepare existing synchronous native/legacy hooks without publishing partial
 * project, selection, history or refresh changes. Commit only changed top-level
 * project fields, preserving the document identity and unrelated subtrees.
 * Legacy hooks must still obey their synchronous, project-local contract.
 */
export function runProjectMutation(label, mutate) {
  if (isPreparingProjectMutation() || S.drag?.active || !S.project) return false;
  const keys = ['project', 'selection', 'ui', 'file', 'history', 'drawingSheets'];
  const original = Object.fromEntries(keys.map(key => [key, S[key]]));
  let staged;
  let next;
  let projectKeys;
  let result;
  let contextChanged = false;
  try {
    staged = {
      project: cloneDeep(original.project),
      selection: cloneDeep(original.selection),
      ui: cloneDeep(original.ui),
      file: cloneDeep(original.file),
      history: { undo: [], redo: [], limit: original.history.limit },
      // Inactive sheets and their history are outside this drawing mutation.
      drawingSheets: freezeTree(cloneDeep(original.drawingSheets))
    };
    copyProjectStateContext(original.project, staged.project);
    Object.assign(S, staged);
    result = prepareProjectMutation(() => mutate());
    if (result && typeof result.then === 'function') {
      Promise.resolve(result).catch(() => undefined);
      return false;
    }
    contextChanged = S.project !== staged.project || S.drawingSheets !== staged.drawingSheets
      || S.history !== staged.history;
    if (contextChanged || !result) return false;
    if (staged.history.undo.length || staged.history.redo.length) return false;
    projectKeys = changedKeys(original.project, staged.project);
    if (!projectKeys.length) return false;
    // Detach the eventual commit from references retained by a hook.
    next = { project: cloneDeep(staged.project), selection: cloneDeep(S.selection), ui: cloneDeep(S.ui) };
  } catch (error) {
    console.warn('[project-mutation] Vorbereitung verworfen.', error);
    return false;
  } finally {
    if (staged) {
      contextChanged ||= S.project !== staged.project || S.drawingSheets !== staged.drawingSheets
        || S.history !== staged.history;
      // Never replace an independently installed project/sheet context. Restore
      // only temporary slots that still belong to this preparation.
      for (const key of keys) {
        if (!contextChanged || S[key] === staged[key]) S[key] = original[key];
      }
    }
  }
  if (contextChanged) return false;
  recordUndo(label);
  for (const key of projectKeys) {
    if (Object.hasOwn(next.project, key)) original.project[key] = next.project[key];
    else delete original.project[key];
  }
  S.selection = next.selection;
  S.ui = next.ui;
  return result;
}
